import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, timer, Subscription } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { SESSION_STORAGE, LOCAL_STORAGE } from './storage';
import { Environment } from '@env/environment';
import { LogService } from './log.service';
import { BaseApiClient } from './base-api-client.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AccessTokenResponseDto } from '../models/access-token-response-dto';
import { SsoLoginModeResponseDto } from '../models/sso-login-mode-response-dto';
import { SsoInitiateResponseDto } from '../models/sso-initiate-response-dto';
import { LoadingIndicatorService } from './loading-indicator.service';
import { UntypedFormGroup } from '@angular/forms';
const httpOptions = {
	headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
	providedIn: 'root',
})
export class AuthService extends BaseApiClient {
	public url: string; // URL to web api
	private tokenSubject: BehaviorSubject<string | null>;
	private tokenExpirationTimer: Subscription | null = null;

	// Observable for isLoggedIn state
	public get isLoggedIn(): Observable<boolean> {
		return this.tokenSubject.asObservable().pipe(
			map((token) => {
				const isLoggedIn = !!token && !this.isTokenExpired(token);
				return isLoggedIn;
			})
		);
	}
	public get accessToken(): Observable<string> {
		return this.tokenSubject.asObservable();
	}

	constructor(
		private readonly http: HttpClient,
		private readonly log: LogService,
		private router: Router,
		@Inject(SESSION_STORAGE) private readonly sessionStorage: Storage,
		@Inject(LOCAL_STORAGE) private readonly localStorage: Storage,
		environment: Environment,
		private loadingIndicatorService: LoadingIndicatorService
	) {
		super();
		this.url = environment.services['employee'];
		const storedToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
		this.tokenSubject = new BehaviorSubject<string | null>(storedToken);
		// Define _isLoggedIn as Observable<boolean>
		this.tokenSubject.asObservable().pipe(
			tap((token) => {
				if (token && !this.isTokenExpired(token)) {
					const expiresInSeconds = this.calculateExpiresIn(token);
					this.initTokenExpirationCheck(expiresInSeconds);
				} else {
					this.logout();
				}
			})
		);
	}

	/**
	 * Check if a JWT token is expired
	 * @param token JWT token to check
	 * @returns True if token is expired, false otherwise
	 */
	private isTokenExpired(token: string | null): boolean {
		if (!token) {
			return true; // Token is considered expired if it's null or undefined
		} else {
			const tokenPayload = this.parseJwt(token);
			const now = Math.floor(Date.now() / 1000); // Convert to seconds for comparison
			return tokenPayload.exp < now;
		}
	}

	/**
	 * Calculate remaining time until token expiration
	 * @param token JWT token to parse
	 * @returns Remaining time in seconds until token expiration
	 */
	private calculateExpiresIn(token: string): number {
		const tokenPayload = this.parseJwt(token);
		const nowInSeconds = Math.floor(Date.now() / 1000); // Current time in UTC seconds
		return tokenPayload.exp - nowInSeconds;
	}

	/**
	 * Parse JWT token payload
	 * @param token JWT token to parse
	 * @returns Parsed token payload as an object
	 */
	private parseJwt(token: string): any {
		try {
			const base64Url = token.split('.')[1];
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			return JSON.parse(atob(base64));
		} catch (e) {
			return {};
		}
	}

	public startAuthentication(returnUrl: string = '/signin'): void {
		this.router.navigateByUrl[returnUrl];
	}
	/**
	 * Sign in user and obtain access token
	 * @param signinData Signin data including username, password, and companyId
	 * @returns Observable with authentication response
	 */
	signin(formGroup: UntypedFormGroup): Observable<AccessTokenResponseDto> {
		let entity = formGroup.value;
		const url = `${this.url}Account/SignIn`;
		const errorHandler = this.createErrorResponseHandler(this.log, formGroup, (_) => this.loadingIndicatorService.stopLoading());

		// No stopLoading on success: the token arriving is not the end of the sign-in as the user
		// experiences it — the app still has to route away from the login screen. The caller stops
		// the overlay once that navigation settles, the same way initiateSso holds it through the
		// redirect to the IdP.
		this.loadingIndicatorService.startLoading();
		return this.http.post<AccessTokenResponseDto>(url, entity, httpOptions).pipe(
			tap((result) => {
				if (result && result.accessToken) {
					const accessToken = result.accessToken;
					const rememberMe = formGroup.value.rememberMe;
					if (rememberMe) {
						localStorage.setItem('accessToken', accessToken);
					} else {
						sessionStorage.setItem('accessToken', accessToken);
					}
					this.tokenSubject.next(accessToken);
					const expirationDurationInSeconds = this.calculateExpiresIn(accessToken);
					this.initTokenExpirationCheck(expirationDurationInSeconds);
				}
			}),
			catchError(errorHandler)
		);
	}

	autoLogin(scheduledAuditId: string, assigneeId: string): Observable<AccessTokenResponseDto> {
		const url = `${this.url}Account/AutoLogin?scheduledAuditId=${scheduledAuditId}&assigneeId=${assigneeId}`;
		const errorHandler = this.createErrorResponseHandler(this.log, null, (_) => this.loadingIndicatorService.stopLoading());

		this.loadingIndicatorService.startLoading();
		return this.http.post<AccessTokenResponseDto>(url, null, httpOptions).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
				if (result && result.accessToken) {
					const accessToken = result.accessToken;

					sessionStorage.setItem('accessToken', accessToken);

					this.tokenSubject.next(accessToken);
					const expirationDurationInSeconds = this.calculateExpiresIn(accessToken);
					this.initTokenExpirationCheck(expirationDurationInSeconds);
				}
			}),
			catchError(errorHandler)
		);
	}
	/**
	 * Log out of the application and clear all storage
	 */
	logout(): void {
		this.sessionStorage.clear();
		this.localStorage.clear();
		this.tokenSubject.next(null);

		// Clear token expiration timer
		if (this.tokenExpirationTimer) {
			this.tokenExpirationTimer.unsubscribe();
			this.tokenExpirationTimer = null;
		}

		this.router.navigate(['/signin']);
	}

	/**
	 * Initialize token expiration check and logout timer
	 * @param expiresIn Expiration time in seconds
	 */
	private initTokenExpirationCheck(expiresIn: number): void {
		// Clear any existing expiration timer to prevent multiple timers
		if (this.tokenExpirationTimer) {
			this.tokenExpirationTimer.unsubscribe();
			this.tokenExpirationTimer = null;
		}

		// Calculate the expiration time in milliseconds
		const expirationTime = expiresIn * 1000; // Convert seconds to milliseconds

		// Set a timer to automatically logout when the token expires
		this.tokenExpirationTimer = timer(expirationTime).subscribe(() => {
			this.logout();
		});
	}
	/**
	 * Check which login mode (standard/dual/sso) is configured for the given company.
	 * @param companyId The EPLoginNumber.Id the user entered
	 */
	checkLoginMode(companyId: number): Observable<SsoLoginModeResponseDto> {
		const url = `${this.url}Account/SsoLoginMode?companyId=${companyId}`;
		const errorHandler = this.createErrorResponseHandler(this.log, null, (_) => this.loadingIndicatorService.stopLoading());

		// The first lookup of the day pays the API cold start, which made Continue look like it
		// had done nothing and invited repeat clicks.
		this.loadingIndicatorService.startLoading();
		return this.http.get<SsoLoginModeResponseDto>(url, httpOptions).pipe(
			tap(() => this.loadingIndicatorService.stopLoading()),
			catchError(errorHandler)
		);
	}

	/**
	 * Begin the SSO flow — returns the IdP redirect URL.
	 * @param companyId The EPLoginNumber.Id the user entered
	 */
	initiateSso(companyId: number): Observable<SsoInitiateResponseDto> {
		const url = `${this.url}Account/SsoInitiate`;
		const errorHandler = this.createErrorResponseHandler(this.log, null, (_) => this.loadingIndicatorService.stopLoading());

		// No stopLoading on success by design: the caller sends the whole tab to the IdP, so the
		// overlay must stay up until the browser leaves the page. SigninComponent clears it if the
		// user comes back with the Back button (bfcache restore).
		this.loadingIndicatorService.startLoading();
		return this.http.post<SsoInitiateResponseDto>(url, { companyId }, httpOptions).pipe(catchError(errorHandler));
	}

	/**
	 * Exchange the one-time SSO callback code for a JWT.  Mirrors autoLogin storage behaviour
	 * (sessionStorage only — SSO sessions don't persist across browser restarts).
	 * @param code The code from the /sso-callback?code= query param
	 */
	ssoExchange(code: string): Observable<AccessTokenResponseDto> {
		const url = `${this.url}Account/SsoExchange`;

		// The landing page after the IdP round trip is blank until this resolves; both branches
		// below already stop the overlay, it was only ever missing its start.
		this.loadingIndicatorService.startLoading();
		return this.http.post<AccessTokenResponseDto>(url, { code }, httpOptions).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
				if (result && result.accessToken) {
					const accessToken = result.accessToken;
					sessionStorage.setItem('accessToken', accessToken);
					this.tokenSubject.next(accessToken);
					const expirationDurationInSeconds = this.calculateExpiresIn(accessToken);
					this.initTokenExpirationCheck(expirationDurationInSeconds);
				}
			}),
			catchError(this.createErrorResponseHandler(this.log, null, (_) => this.loadingIndicatorService.stopLoading()))
		);
	}

	/**
	 * Get current access token from localStorage
	 * @returns Access token or null if not found
	 */
	getAccessToken(): string | null {
		return this.tokenSubject.value;
	}

	/**
	 * Check if user is authenticated based on access token presence
	 * @returns True if user is authenticated, false otherwise
	 */
	isAuthenticated(): boolean {
		return !!this.getAccessToken();
	}
}
